import React, { useMemo, useRef, useState } from "react";
import { useInstantSearch, useRefinementList } from "react-instantsearch";

// Backed by the "location.lvl1" facet whose values are "Continent > Country". We split on " > "
// to group countries under their continent, so the frontend needs no country-to-continent map.
// Refinement is always leaf-level (individual countries); a region's checkbox toggles all of its
// countries, and its tri-state is derived from how many are refined.

const ATTRIBUTE = "location.lvl1";

const boxBase = {
  width: 16,
  height: 16,
  borderRadius: 4,
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
};

const RESET_ICON_PATH =
  "M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z";

const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

function Checkbox({ state }) {
  if (state === "all") {
    return <span style={{ ...boxBase, background: "#533dd1", color: "#fff", fontSize: 11 }}>✓</span>;
  }
  if (state === "some") {
    return (
      <span style={{ ...boxBase, border: "1.5px solid #533dd1", background: "#fff" }}>
        <span style={{ width: 8, height: 2, background: "#533dd1", borderRadius: 1 }} />
      </span>
    );
  }
  return <span style={{ ...boxBase, border: "1.5px solid #cbd0d6" }} />;
}

const onEnter = (fn) => (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    fn();
  }
};

function RegionCountryRefinement() {
  const { items, refine } = useRefinementList({ attribute: ATTRIBUTE, limit: 300 });
  const { results, indexUiState, setIndexUiState } = useInstantSearch();
  const [query, setQuery] = useState("");
  const queryInputRef = useRef(null);
  const [collapsed, setCollapsed] = useState({});

  const groups = useMemo(() => {
    const byRegion = new Map();
    items.forEach((item) => {
      const sep = item.label.indexOf(" > ");
      if (sep === -1) return;
      const region = item.label.slice(0, sep);
      const country = item.label.slice(sep + 3);
      if (!byRegion.has(region)) byRegion.set(region, []);
      byRegion.get(region).push({ value: item.value, country, isRefined: item.isRefined });
    });
    return [...byRegion.entries()]
      .map(([region, countries]) => ({
        region,
        countries: countries.sort((a, b) => a.country.localeCompare(b.country)),
      }))
      .sort((a, b) => a.region.localeCompare(b.region));
  }, [items]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? groups
        .map((g) => ({
          ...g,
          countries: g.countries.filter(
            (c) => c.country.toLowerCase().includes(q) || g.region.toLowerCase().includes(q),
          ),
        }))
        .filter((g) => g.countries.length > 0)
    : groups;

  const regionState = (countries) => {
    const refined = countries.filter((c) => c.isRefined).length;
    if (refined === 0) return "none";
    return refined === countries.length ? "all" : "some";
  };

  // One UI-state update rather than a refine() per country: each refine() fires its own search.
  const toggleRegion = (countries) => {
    const selectAll = regionState(countries) !== "all";
    const regionValues = new Set(countries.map((c) => c.value));
    setIndexUiState(({ page, ...state }) => {
      const current = state.refinementList?.[ATTRIBUTE] ?? [];
      const next = selectAll
        ? [...new Set([...current, ...regionValues])]
        : current.filter((value) => !regionValues.has(value));
      return { ...state, refinementList: { ...state.refinementList, [ATTRIBUTE]: next } };
    });
  };

  const selectedCount = indexUiState?.refinementList?.[ATTRIBUTE]?.length ?? 0;
  const resultCount = results?.nbHits;

  const clearAll = () => {
    setIndexUiState(({ page, ...state }) => {
      const { [ATTRIBUTE]: cleared, ...refinementList } = state.refinementList ?? {};
      return { ...state, refinementList };
    });
  };

  const collapsedFor = (region, state) => {
    if (q) return false;
    if (collapsed[region] !== undefined) return collapsed[region];
    return state === "none";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ position: "relative", padding: "0 12px 8px" }}>
        <input
          ref={queryInputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search regions or countries"
          style={{
            width: "100%",
            height: 36,
            boxSizing: "border-box",
            border: "1px solid #d2d3d4",
            borderRadius: 8,
            padding: "0 32px 0 12px",
            fontSize: 14,
          }}
        />
        {query && (
          <button
            type="button"
            title="Clear the search query"
            aria-label="Clear the search query"
            onClick={() => {
              setQuery("");
              queryInputRef.current?.focus();
            }}
            style={{
              position: "absolute",
              top: 8,
              right: 20,
              width: 20,
              height: 20,
              padding: 0,
              border: 0,
              background: "transparent",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <svg viewBox="0 0 20 20" width="10" height="10" aria-hidden="true">
              <path d={RESET_ICON_PATH} fill="#1d1452" />
            </svg>
          </button>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          minHeight: 20,
          padding: "0 12px 8px",
          fontSize: 13,
        }}
      >
        <span style={{ fontStyle: "italic", color: "#6b7280" }}>
          {resultCount !== undefined &&
            `${resultCount.toLocaleString("en-US")} ${resultCount === 1 ? "Result" : "Results"}`}
        </span>
        {selectedCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              color: "#533dd1",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Clear All
          </button>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "0 8px 8px" }}>
        {groups.length === 0 && (
          <div style={{ padding: 12, fontSize: 13, color: "#6b7280" }}>No locations for the current filters.</div>
        )}
        {groups.length > 0 && visible.length === 0 && (
          <div style={{ padding: 12, fontSize: 13, color: "#6b7280" }}>No matches.</div>
        )}
        {visible.map(({ region, countries }) => {
          const state = regionState(countries);
          const isCollapsed = collapsedFor(region, state);
          return (
            <div key={region}>
              <div style={{ display: "flex", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Select all of ${region}`}
                  onClick={() => toggleRegion(countries)}
                  onKeyDown={onEnter(() => toggleRegion(countries))}
                  className="sd-loc-hit"
                  style={{ display: "flex", alignItems: "center", padding: "8px 8px 8px 6px", cursor: "pointer" }}
                >
                  <Checkbox state={state} />
                </span>
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={!isCollapsed}
                  onClick={() => setCollapsed((c) => ({ ...c, [region]: !isCollapsed }))}
                  onKeyDown={onEnter(() => setCollapsed((c) => ({ ...c, [region]: !isCollapsed })))}
                  className="sd-loc-hit"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    minWidth: 0,
                    gap: 8,
                    padding: "8px 8px 8px 4px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#374151", ...ellipsis }}>{region}</span>
                  <span style={{ marginLeft: "auto", color: "#9aa1a9", fontSize: 11 }}>{isCollapsed ? "▸" : "▾"}</span>
                </div>
              </div>
              {!isCollapsed &&
                countries.map((c) => (
                  <div
                    key={c.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => refine(c.value)}
                    onKeyDown={onEnter(() => refine(c.value))}
                    className="sd-loc-hit"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "6px 8px 6px 34px",
                      cursor: "pointer",
                      fontSize: 14,
                      color: "#21272a",
                    }}
                  >
                    <Checkbox state={c.isRefined ? "all" : "none"} />
                    <span style={ellipsis}>{c.country}</span>
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RegionCountryRefinement;
